'use client';

import { FC, useState, useEffect } from 'react';
import {
  AdditionalItem,
  AdditionalItemEditorProps,
} from './AdditionalItemEditor.types';
import {
  Button,
  ButtonGroup,
  Input,
  Text,
  Flex,
  Heading,
  Icon,
  Pill,
} from '@/_components';

export const AdditionalItemEditor: FC<AdditionalItemEditorProps> = ({
  items,
  rolloverItems,
  onCreate,
  onSaveEdit,
  onRemove,
}) => {
  const [text, setText] = useState<string>('');
  const [editItem, setEditItem] = useState<AdditionalItem | null>(null);
  const [itemsCopy, setItemsCopy] = useState<Array<string>>(items);

  const onCreateItem = () => {
    onCreate(text);
    setText('');
  };

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setText(event.target.value);

  const onEditItemTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: number,
  ) => {
    if (editItem?.key !== key) return;
    const newText = event.target.value;
    const newItems = [...itemsCopy];
    newItems[key] = newText;
    setItemsCopy(newItems);
    setEditItem({ ...editItem, text: newText });
  };

  useEffect(() => {
    return () => {
      setText('');
    };
  }, []);

  useEffect(() => {
    setItemsCopy(items);
  }, [items]);

  return (
    <Flex isColumn={true} gap={6}>
      <Heading level={4}>Additional Items</Heading>

      {itemsCopy?.map((item, key) => {
        const onRemoveItem = () => onRemove(item);
        const onEditItem = () => {
          setEditItem({ text: item, key });
        };
        const onSaveEditItem = () => {
          if (editItem) {
            onSaveEdit(editItem);
            setEditItem(null);
          }
        };
        const isDuplicateRollover: boolean = !!rolloverItems?.some(
          (rollover) => rollover.name === item,
        );
        const isDuplicateItem: boolean =
          itemsCopy.filter((i) => i === item).length > 1;
        const isBeingEdited: boolean = editItem?.key === key;

        return (
          <Flex key={key} isBetween={true} gap={6}>
            <Flex isYCentered={true} gap={4} isFullWidth>
              <Icon size={16} color="secondary" name="arrow-bend-down-right" />
              {editItem?.key === key ? (
                <Input
                  value={editItem.text}
                  onChange={(event) =>
                    onEditItemTextChange(event, editItem.key)
                  }
                  label="Edit Item"
                  isLabelHidden={true}
                  helperText={isDuplicateItem ? 'Item already exists' : ''}
                  helperTextColor="critical"
                />
              ) : (
                <>
                  <Text type="small">{item}</Text>
                </>
              )}
            </Flex>
            <ButtonGroup isSameLine={true}>
              {isDuplicateRollover && (
                <Pill size="small" intent="danger" label={'duplicate'}></Pill>
              )}
              <Button
                value={isBeingEdited ? 'Save' : 'Edit'}
                size="tiny"
                isIconOnly={true}
                intent="ghost"
                withTooltip={true}
                isDisabled={isDuplicateItem}
                iconLeft={isBeingEdited ? 'check' : 'pencil'}
                onClick={isBeingEdited ? onSaveEditItem : onEditItem}
              />
              <Button
                value="Remove"
                size="tiny"
                isIconOnly={true}
                isDisabled={isBeingEdited}
                intent="danger-ghost"
                withTooltip={true}
                iconLeft="trash"
                onClick={onRemoveItem}
              />
            </ButtonGroup>
          </Flex>
        );
      })}
      {editItem === null && (
        <Flex isYCentered={true} gap={6}>
          <Input
            value={text}
            placeholder="New Item"
            onChange={onTextChange}
            label="New Item"
            isLabelHidden={true}
            helperText={
              itemsCopy.includes(text.trim()) ? 'Item already exists' : ''
            }
            helperTextColor="critical"
          />
          <ButtonGroup align="right">
            <Button
              value="Create New"
              iconLeft="plus"
              size="small"
              intent="secondary"
              isDisabled={!text.trim() || itemsCopy.includes(text.trim())}
              onClick={onCreateItem}
            />
          </ButtonGroup>
        </Flex>
      )}
    </Flex>
  );
};
